import * as React from 'react'

import { Box, Button, Icon, List, Text } from '@factorialco/deprecated-design-system'
import { RemoveLegalEntityPageQuery } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import policyPeriods from 'modules/payroll_bundle/store/collections/policyPeriods'

type ModalProps = React.ComponentProps<typeof Modal>

interface Props {
  supplements: RemoveLegalEntityPageQuery['payroll']['supplements']
  legalEntity: LegalEntity
  onClose: ModalProps['onClose']
  onSubmit: ModalProps['onSubmit']
}

const RemoveLegalEntityWithDependencies = ({
  supplements,
  legalEntity,
  onClose,
  onSubmit,
}: Props) => {
  const { loading } = useAsyncData(async () => {
    policyPeriods.fetch()
  }, [])

  const supplementAmount = (amount_in_cents: number, unit: string) => {
    const amount = amount_in_cents / 100
    if (unit === 'units') return amount + unit

    return amount + legalEntity.attributes.currency
  }

  const policyPeriodMonth = (policy_period_id: number) => {
    const startsOn = policyPeriods.get(policy_period_id)?.attributes.starts_on
    const periodMonth = new Date(startsOn).toLocaleString('default', { month: 'long' })
    return periodMonth
  }

  return (
    <Modal
      illustration="alert"
      title={i18n.t('settings.legal_entities.legal_entity.remove.title', {
        legal_entity: legalEntity.name,
      })}
      description={i18n.t('settings.legal_entities.legal_entity.remove.with_dependency', {
        legal_entity: legalEntity.name,
        no_of_supplements: supplements.length,
      })}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('settings.legal_entities.legal_entity.remove.back')}
          </Button>
          <Button type="negative" submit>
            {i18n.t('settings.legal_entities.legal_entity.remove.submit')}
          </Button>
        </>
      }
    >
      {!loading && (
        <List>
          {supplements.slice(0, 3).map((el) => (
            <List.Item key={el.id}>
              <Box flexDirection="row">
                <Box paddingRight="s12">
                  <Icon.Medium icon="delete" />
                </Box>
                <Text.Normal color="grey700" weight="medium" size="300">
                  {i18n.t('settings.legal_entities.legal_entity.remove.name_amount_in_month', {
                    name: el.employee?.fullName,
                    amount: supplementAmount(Number(el.amountInCents), el.unit),
                    taxonomy_name: el.taxonomy?.name,
                    month: el.policyPeriod?.id ? policyPeriodMonth(el.policyPeriod?.id) : '',
                  })}
                </Text.Normal>
              </Box>
            </List.Item>
          ))}
        </List>
      )}
      <Box paddingTop="s6">
        {supplements.length > 3 && (
          <Text.Normal color="grey700">
            {i18n.t('settings.legal_entities.legal_entity.remove.remaining_supplements', {
              remaining_supplements_count: supplements.length - 3,
            })}
          </Text.Normal>
        )}
      </Box>
    </Modal>
  )
}

export default RemoveLegalEntityWithDependencies
